import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useApi } from '../../providers/api-provider';
import { UserPromptDialog } from './prompt-dialog';
import { PolicyContent } from '../policy-content';
import { policies } from '../../content/policies';

export function ModuleUseNotice({ moduleKey, userKey, onApproved, onCancel }: {
    moduleKey: string | null; userKey: string;
    onApproved: (key: string) => Promise<void>; onCancel: () => void;
}) {
    const api = useApi();
    const [phase, setPhase] = useState<'checking' | 'notice' | 'saving' | 'opening' | 'error'>('checking');
    const [error, setError] = useState('');
    const [unavailable, setUnavailable] = useState(false);
    const [waitingOnPrerequisites, setWaitingOnPrerequisites] = useState(false);
    const [retry, setRetry] = useState(0);
    const generation = useRef(0);
    const saving = useRef(false);
    const approved = useRef(onApproved);
    approved.current = onApproved;
    const currentApi = useRef(api);
    currentApi.current = api;
    // Auth token updates may replace the client; only readiness or a new selection starts a check.
    const apiReady = !!api;
    const showFailure = (failure: any, fallback: string) => {
        const status = failure?.response?.status;
        setUnavailable(status === 403 || status === 404);
        setWaitingOnPrerequisites(failure?.response?.data?.code === 'module_prerequisites_not_met');
        setError(failure?.response?.data?.message || failure?.message || fallback);
        setPhase('error');
    };
    const reportFailure = useRef(showFailure);
    reportFailure.current = showFailure;
    useEffect(() => {
        const attempt = ++generation.current;
        const controller = new AbortController();
        saving.current = false; setError(''); setUnavailable(false); setWaitingOnPrerequisites(false); setPhase('checking');
        if (moduleKey && currentApi.current && userKey) {
            currentApi.current.getModuleUseAgreement(moduleKey, controller.signal).then(async result => {
                if (attempt !== generation.current || controller.signal.aborted) return;
                if (result?.accepted === true && result.accepted_at) {
                    setPhase('opening'); await approved.current(moduleKey);
                } else if (result?.accepted === false && result.notice_version === policies.courseUse.version) setPhase('notice');
                else throw new Error('The notice could not be verified. Please reload the page.');
            }).catch(failure => {
                if (attempt !== generation.current || controller.signal.aborted) return;
                reportFailure.current(failure, 'Unable to check your agreement. Please try again.');
            });
        }
        return () => { controller.abort(); generation.current++; };
    }, [moduleKey, userKey, apiReady, retry]);

    const agree = async () => {
        if (!api || !moduleKey || saving.current || phase !== 'notice' || error) return;
        const attempt = generation.current;
        saving.current = true; setPhase('saving');
        try {
            const result = await api.acceptModuleUseAgreement(moduleKey, policies.courseUse.version);
            if (attempt !== generation.current) return;
            if (result?.accepted !== true || !result.accepted_at) throw new Error('Your agreement was not saved. Please try again.');
            setPhase('opening'); await approved.current(moduleKey);
        } catch (failure: any) {
            if (attempt === generation.current) {
                showFailure(failure, 'Unable to save your agreement. Please try again.');
            }
        } finally { if (attempt === generation.current) saving.current = false; }
    };
    const cancel = () => { if (phase !== 'saving' && phase !== 'opening') onCancel(); };
    return <>
        {moduleKey && (phase === 'checking' || phase === 'opening') && <p role="status">{phase === 'checking' ? 'Checking module access…' : 'Opening module…'}</p>}
        {moduleKey && phase === 'checking' && <Button label="Cancel" text onClick={cancel} />}
        <UserPromptDialog visible={!!moduleKey && (phase === 'notice' || phase === 'saving')} title={policies.courseUse.title}
            question="Please read this notice before opening the module. Agree confirms that you have read and understood it."
            onClose={cancel} actions={[
                <Button key="cancel" label="Cancel" text disabled={phase === 'saving'} onClick={cancel} />,
                <Button key="agree" label="Agree" loading={phase === 'saving'} disabled={phase === 'saving'} onClick={() => void agree()} />,
            ]}>
            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}><PolicyContent policy="courseUse" /></div>
        </UserPromptDialog>
        <UserPromptDialog visible={!!moduleKey && phase === 'error'}
            title={waitingOnPrerequisites ? 'Waiting on prerequisites' : unavailable ? 'Lesson unavailable' : 'Unable to verify lesson access'}
            question={waitingOnPrerequisites ? 'Complete the required lessons or courses before opening this lesson.'
                : unavailable ? 'This lesson is unavailable or you do not have permission to access it.' : 'Please try again or close this message.'}
            onClose={cancel} actions={[
                <Button key="close" label="Close" onClick={cancel} />,
                ...(!unavailable ? [<Button key="retry" label="Retry" onClick={() => setRetry(current => current + 1)} />] : []),
            ]}>
            {error && <p role="alert">{error}</p>}
        </UserPromptDialog>
    </>;
}
